import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Banner from "../components/Banner";
import { useAuth } from "../contexts/Auth";
import api from "../helpers/api";

const SubscriptionPlans: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const hasActiveSubscription = user?.hasActiveSubscription;

  const handleGetStarted = () => {
    sessionStorage.setItem("redirectAfterLogin", "/plans");
    navigate("/signup");
  };

  const handleCheckout = async () => {
    try {
      const response = await api.post("/subscription/checkout");
      const { url } = response.data;

      if (url) {
        window.location.href = url;
      } else {
        console.error("No checkout URL received from the server");
      }
    } catch (error) {
      console.error("Error initiating checkout");
    } finally {
      setIsLoading(false);
    }
  };

  const buttonStyles = {
    contained: {
      background:
        "linear-gradient(45deg,rgba(10, 55, 113, 0.6) 30%,rgba(17, 83, 158, 0.6) 90%)",
      borderRadius: "50px",
      padding: "12px 35px",
      mt: -3,
      textTransform: "none",
      fontSize: "1.1rem",
      fontWeight: 600,
      border: "1px solid rgba(255, 255, 255, 0.2)",
      boxShadow: "0 0 15px rgba(255, 255, 255, 0.2)",
      "&:hover": {
        background: "linear-gradient(45deg, #0A2A4A 30%, #041C2C 90%)",
        boxShadow: "0 0 20px rgba(255, 255, 255, 0.3)",
        border: "1px solid rgba(255, 255, 255, 0.3)",
      },
      "&.Mui-disabled": {
        color: "rgba(227, 217, 214, 0.5)",
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      },
    },
  };

  const plans = [
    {
      title: "Free",
      price: "$0/month",
      features: [
        { name: "Track up to 3 events", included: true },
        { name: "Real-time notifications", included: true },
        { name: "Analytics", included: true },
      ],
    },
    {
      title: "Pro",
      price: "$4.99/month",
      features: [
        { name: "Track up to 10 events", included: true },
        { name: "Real-time notifications", included: true },
        { name: "Analytics", included: true },
        {
          name: "Filter alerts by Seat Location",
          included: true,
        },
        { name: "Increased frequency price checks", included: true },
        {
          name: "WhatsApp Alerts",
          included: true,
        },
      ],
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        background: `
        url('/event-background.webp'),
        radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)
      `,
        backgroundBlendMode: "soft-light, normal",
        backgroundSize: "cover, cover, cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        color: "#E3D9D6",
        pb: 20,
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.05)",
          pointerEvents: "none",
        },
      }}
    >
      <>
        {/* Meta tags for SEO */}
        <Helmet>
          <title>Plans | Event Spy</title>
          <meta
            name="description"
            content="Compare our flexible subscription plans and unlock additional features to help you stay ahead of ticket prices"
          />
        </Helmet>
      </>
      <Banner />
      <Box sx={{ pt: "80px", pb: 4 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h1"
            align="center"
            gutterBottom
            fontWeight={700}
            sx={{ mb: 2, pt: "50px" }}
          >
            Choose Your Plan
          </Typography>
          <Typography variant="h6" align="center" gutterBottom sx={{ mb: 4 }}>
            Select the plan that best fits your needs
          </Typography>

          <Grid container spacing={{ xs: 6, md: 4 }} justifyContent="center">
            {plans.map((plan) => (
              <Grid item xs={12} md={6} key={plan.title}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 4 },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    background:
                      plan.title === "Pro"
                        ? "linear-gradient(135deg, rgba(74, 157, 255, 0.15) 0%, rgba(255,255,255,0.05) 100%)"
                        : "rgba(255, 255, 255, 0.05)",
                    backdropFilter: "blur(10px)",
                    border:
                      plan.title === "Pro"
                        ? "2px solid rgba(74, 157, 255, 0.3)"
                        : "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 3,
                    transition: "all 0.3s ease-in-out",
                    position: "relative",
                    "&:hover": {
                      boxShadow:
                        plan.title === "Pro"
                          ? "0 12px 20px rgba(74, 157, 255, 0.2), 0 0 30px rgba(74, 157, 255, 0.15)"
                          : "0 12px 20px rgba(0,0,0,0.2)",
                      border:
                        plan.title === "Pro"
                          ? "2px solid rgba(74, 157, 255, 0.5)"
                          : "1px solid rgba(255,255,255,0.3)",
                    },
                    ...(plan.title === "Pro" && {
                      boxShadow: "0 8px 16px rgba(74, 157, 255, 0.15)",
                    }),
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h4"
                      component="h2"
                      align="center"
                      gutterBottom
                      sx={{
                        color: "#E3D9D6",
                        fontWeight: 700,
                        mb: 3,
                      }}
                    >
                      {plan.title}
                    </Typography>
                    <Typography
                      variant="h5"
                      component="p"
                      align="center"
                      gutterBottom
                      sx={{
                        color: plan.title === "Pro" ? "#4A9DFF" : "#E3D9D6",
                        fontWeight: 600,
                      }}
                    >
                      {plan.price}
                    </Typography>

                    <Typography
                      variant="body2"
                      align="center"
                      sx={{
                        mt: -1.3,
                        color:
                          plan.title === "Pro"
                            ? "rgba(255, 255, 255, 0.5)"
                            : "transparent",
                      }}
                    >
                      Cancel anytime
                    </Typography>

                    <List sx={{ mb: 1 }}>
                      {plan.features.map((feature, index) => {
                        const match = feature.name.match(/^(.*?)(\s*\(.*\))?$/);
                        const mainText = match?.[1] || feature.name;
                        const subpoint = match?.[2];

                        return (
                          <ListItem key={index} sx={{ py: 1 }}>
                            <ListItemIcon>
                              {feature.included ? (
                                <CheckIcon sx={{ color: "#4CAF50" }} />
                              ) : (
                                <CloseIcon
                                  sx={{ color: "rgba(255,255,255,0.3)" }}
                                />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <>
                                  {mainText}
                                  {subpoint && (
                                    <Typography
                                      component="span"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        fontSize: "0.8em",
                                      }}
                                    >
                                      {subpoint}
                                    </Typography>
                                  )}
                                </>
                              }
                              sx={{
                                color: feature.included
                                  ? "#E3D9D6"
                                  : "rgba(255,255,255,0.5)",
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                  <Box
                    sx={{
                      textAlign: "center",
                      mt: { xs: -1, md: -6 },
                    }}
                  >
                    {!user ? (
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleGetStarted}
                        sx={buttonStyles.contained}
                      >
                        Get Started
                      </Button>
                    ) : hasActiveSubscription ? (
                      <Button
                        variant="contained"
                        size="large"
                        disabled
                        sx={buttonStyles.contained}
                      >
                        {plan.title === "Pro" ? "Current Plan" : "Included"}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() =>
                          plan.title === "Pro" ? handleCheckout() : null
                        }
                        disabled={plan.title !== "Pro" || isLoading}
                        sx={buttonStyles.contained}
                      >
                        {plan.title === "Pro" ? "Upgrade Now" : "Current Plan"}
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default SubscriptionPlans;
